import React, { useState, useEffect, useCallback } from 'react';
import Table from 'react-bootstrap/Table';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import { styles } from '../../styles/styles.js';
import {
  getOrders, approveOrder, rejectOrder, cancelOrder,
  modifyOrder, batchApprove, batchReject,
} from '../../services/tradingApi';

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'prepared', label: 'Prepared' },
  { value: 'approved', label: 'Approved' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'filled', label: 'Filled' },
  { value: 'settled_win', label: 'Settled (Win)' },
  { value: 'settled_loss', label: 'Settled (Loss)' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'expired', label: 'Expired' },
];

const BET_TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'moneyline', label: 'Moneyline' },
  { value: 'spread', label: 'Spread' },
  { value: 'total', label: 'Total' },
];

const statusColor = (s) => {
  if (s === 'prepared') return '#ff9800';
  if (s === 'approved') return '#2196f3';
  if (s === 'submitted') return '#9c27b0';
  if (s === 'filled') return '#00bcd4';
  if (s === 'settled_win') return '#4caf50';
  if (s === 'settled_loss') return '#f44336';
  if (s === 'rejected' || s === 'cancelled' || s === 'expired') return '#888';
  return 'white';
};

const OrderQueue = () => {
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());

  // Filters
  const [statusFilter, setStatusFilter] = useState('prepared');
  const [betTypeFilter, setBetTypeFilter] = useState('');
  const [offset, setOffset] = useState(0);
  const limit = 50;

  // Modals
  const [rejectModalOrder, setRejectModalOrder] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [modifyModalOrder, setModifyModalOrder] = useState(null);
  const [modifyPrice, setModifyPrice] = useState('');
  const [modifyContracts, setModifyContracts] = useState('');
  const [batchRejectModal, setBatchRejectModal] = useState(false);
  const [batchRejectReason, setBatchRejectReason] = useState('');

  const fetchOrders = useCallback(async () => {
    try {
      const params = { limit, offset };
      if (statusFilter) params.status = statusFilter;
      if (betTypeFilter) params.bet_type = betTypeFilter;
      const { data } = await getOrders(params);
      setOrders(data.results);
      setTotal(data.total);
    } catch (err) {
      console.error('Error fetching orders:', err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, betTypeFilter, offset]);

  useEffect(() => {
    fetchOrders();
    const interval = setInterval(fetchOrders, 30000);
    return () => clearInterval(interval);
  }, [fetchOrders]);

  const handleApprove = async (id) => {
    await approveOrder(id);
    fetchOrders();
  };

  const handleRejectSubmit = async () => {
    if (rejectModalOrder) {
      await rejectOrder(rejectModalOrder, rejectReason);
      setRejectModalOrder(null);
      setRejectReason('');
      fetchOrders();
    }
  };

  const handleCancel = async (id) => {
    await cancelOrder(id);
    fetchOrders();
  };

  const handleModifySubmit = async () => {
    if (modifyModalOrder) {
      const data = {};
      if (modifyPrice) data.limit_price = parseInt(modifyPrice);
      if (modifyContracts) data.contracts = parseInt(modifyContracts);
      await modifyOrder(modifyModalOrder, data);
      setModifyModalOrder(null);
      fetchOrders();
    }
  };

  const handleBatchApprove = async () => {
    if (selected.size === 0) return;
    await batchApprove([...selected]);
    setSelected(new Set());
    fetchOrders();
  };

  const handleBatchRejectSubmit = async () => {
    if (selected.size === 0) return;
    await batchReject([...selected], batchRejectReason);
    setSelected(new Set());
    setBatchRejectModal(false);
    setBatchRejectReason('');
    fetchOrders();
  };

  const toggleSelect = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const toggleSelectAll = () => {
    if (selected.size === orders.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(orders.map(o => o.id)));
    }
  };

  const timeAgo = (ts) => {
    if (!ts) return '--';
    const diff = Date.now() - new Date(ts).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  return (
    <div style={{ padding: '1rem', maxWidth: '1400px' }}>
      <h2 style={{ color: 'white', marginBottom: '20px' }}>Order Queue</h2>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '15px', flexWrap: 'wrap' }}>
        <Form.Select
          size="sm"
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setOffset(0); }}
          style={{ width: '160px', backgroundColor: '#111', color: 'white', border: '1px solid #444' }}
        >
          {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </Form.Select>
        <Form.Select
          size="sm"
          value={betTypeFilter}
          onChange={(e) => { setBetTypeFilter(e.target.value); setOffset(0); }}
          style={{ width: '140px', backgroundColor: '#111', color: 'white', border: '1px solid #444' }}
        >
          {BET_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </Form.Select>
      </div>

      {/* Batch actions */}
      {selected.size > 0 && (
        <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
          <Button size="sm" variant="outline-success" onClick={handleBatchApprove}>
            Approve {selected.size} Selected
          </Button>
          <Button size="sm" variant="outline-danger" onClick={() => setBatchRejectModal(true)}>
            Reject {selected.size} Selected
          </Button>
        </div>
      )}

      {loading ? (
        <div style={{ color: 'white' }}>Loading...</div>
      ) : orders.length === 0 ? (
        <div style={{ color: '#888', padding: '20px' }}>No orders found</div>
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <Table bordered size="sm" style={{ backgroundColor: 'black', color: 'white', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #444' }}>
                  <th style={{ width: '30px' }}>
                    <input type="checkbox" checked={selected.size === orders.length} onChange={toggleSelectAll} />
                  </th>
                  <th>Fixture</th>
                  <th>League</th>
                  <th>Type</th>
                  <th>Side</th>
                  <th>Price</th>
                  <th>Qty</th>
                  <th>Cost</th>
                  <th>Edge</th>
                  <th>Strategy</th>
                  <th>Status</th>
                  <th>Time</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id} style={{ borderBottom: '1px solid #222' }}>
                    <td><input type="checkbox" checked={selected.has(o.id)} onChange={() => toggleSelect(o.id)} /></td>
                    <td>{o.fixture_summary}</td>
                    <td>{o.league || '--'}</td>
                    <td>{o.bet_type}</td>
                    <td>{o.side?.toUpperCase()}</td>
                    <td>{o.limit_price}¢</td>
                    <td>{o.contracts}</td>
                    <td>${parseFloat(o.cost).toFixed(2)}</td>
                    <td>{(parseFloat(o.tfb_edge) * 100).toFixed(1)}%</td>
                    <td>{o.strategy_name}</td>
                    <td style={{ color: statusColor(o.status) }}>{o.status}</td>
                    <td style={{ color: '#888' }}>{timeAgo(o.prepared_at)}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        {o.status === 'prepared' && (
                          <>
                            <Button size="sm" variant="outline-success" onClick={() => handleApprove(o.id)} style={{ fontSize: '11px', padding: '1px 6px' }}>
                              Approve
                            </Button>
                            <Button size="sm" variant="outline-danger" onClick={() => setRejectModalOrder(o.id)} style={{ fontSize: '11px', padding: '1px 6px' }}>
                              Reject
                            </Button>
                            <Button size="sm" variant="outline-secondary" onClick={() => { setModifyModalOrder(o.id); setModifyPrice(o.limit_price); setModifyContracts(o.contracts); }} style={{ fontSize: '11px', padding: '1px 6px' }}>
                              Edit
                            </Button>
                          </>
                        )}
                        {['prepared', 'approved', 'submitted'].includes(o.status) && (
                          <Button size="sm" variant="outline-warning" onClick={() => handleCancel(o.id)} style={{ fontSize: '11px', padding: '1px 6px' }}>
                            Cancel
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {/* Pagination */}
          <div style={{ display: 'flex', justifyContent: 'space-between', color: '#888', fontSize: '13px', marginTop: '10px' }}>
            <span>Showing {offset + 1}-{Math.min(offset + limit, total)} of {total}</span>
            <div style={{ display: 'flex', gap: '10px' }}>
              <Button size="sm" variant="outline-secondary" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))}>
                Prev
              </Button>
              <Button size="sm" variant="outline-secondary" disabled={offset + limit >= total} onClick={() => setOffset(offset + limit)}>
                Next
              </Button>
            </div>
          </div>
        </>
      )}

      {/* Reject modal */}
      <Modal show={!!rejectModalOrder} onHide={() => setRejectModalOrder(null)} centered>
        <Modal.Header closeButton style={{ backgroundColor: '#111', borderColor: '#444' }}>
          <Modal.Title style={{ color: 'white' }}>Reject Order</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: '#111' }}>
          <Form.Control
            as="textarea"
            rows={3}
            placeholder="Rejection reason (optional)"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            style={{ backgroundColor: '#222', color: 'white', border: '1px solid #444' }}
          />
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: '#111', borderColor: '#444' }}>
          <Button variant="secondary" onClick={() => setRejectModalOrder(null)}>Cancel</Button>
          <Button variant="danger" onClick={handleRejectSubmit}>Reject</Button>
        </Modal.Footer>
      </Modal>

      {/* Modify modal */}
      <Modal show={!!modifyModalOrder} onHide={() => setModifyModalOrder(null)} centered>
        <Modal.Header closeButton style={{ backgroundColor: '#111', borderColor: '#444' }}>
          <Modal.Title style={{ color: 'white' }}>Modify Order</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: '#111' }}>
          <Form.Group className="mb-3">
            <Form.Label style={{ color: 'white' }}>Limit Price (cents)</Form.Label>
            <Form.Control
              type="number"
              value={modifyPrice}
              onChange={(e) => setModifyPrice(e.target.value)}
              style={{ backgroundColor: '#222', color: 'white', border: '1px solid #444' }}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label style={{ color: 'white' }}>Contracts</Form.Label>
            <Form.Control
              type="number"
              value={modifyContracts}
              onChange={(e) => setModifyContracts(e.target.value)}
              style={{ backgroundColor: '#222', color: 'white', border: '1px solid #444' }}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: '#111', borderColor: '#444' }}>
          <Button variant="secondary" onClick={() => setModifyModalOrder(null)}>Cancel</Button>
          <Button variant="primary" onClick={handleModifySubmit}>Save</Button>
        </Modal.Footer>
      </Modal>

      {/* Batch reject modal */}
      <Modal show={batchRejectModal} onHide={() => setBatchRejectModal(false)} centered>
        <Modal.Header closeButton style={{ backgroundColor: '#111', borderColor: '#444' }}>
          <Modal.Title style={{ color: 'white' }}>Reject {selected.size} Orders</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: '#111' }}>
          <Form.Control
            as="textarea"
            rows={3}
            placeholder="Rejection reason (optional)"
            value={batchRejectReason}
            onChange={(e) => setBatchRejectReason(e.target.value)}
            style={{ backgroundColor: '#222', color: 'white', border: '1px solid #444' }}
          />
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: '#111', borderColor: '#444' }}>
          <Button variant="secondary" onClick={() => setBatchRejectModal(false)}>Cancel</Button>
          <Button variant="danger" onClick={handleBatchRejectSubmit}>Reject All</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default OrderQueue;
